import {
  boolean,
  index,
  integer,
  jsonb,
  pgTable,
  text,
  timestamp,
  uuid,
  varchar,
} from 'drizzle-orm/pg-core';
import { id, timestamps } from './_shared';

/**
 * Watchlist sources for public-source job discovery. Replaces the earlier
 * Gmail-readonly approach: the platform never reads the user's primary
 * inbox. Sources include Seek RSS feeds (user creates a saved search,
 * gives us the RSS URL), Greenhouse + Lever boards (e.g. anthropic,
 * atlassian), generic RSS feeds (gov / industry), and the Adzuna +
 * USAJobs public APIs (breadth aggregators — see WatchlistPollingService).
 */
export const watchlistSources = pgTable(
  'watchlist_sources',
  {
    id: id(),
    userId: text('user_id').notNull(),
    /** seek_rss | greenhouse_board | lever_board | rss_generic | adzuna | usajobs | discovery_email */
    kind: varchar('kind', { length: 32 }).notNull(),
    /** Human-readable label for the UI list. */
    label: varchar('label', { length: 200 }).notNull(),
    /**
     * Per-kind config:
     *  - seek_rss / rss_generic:  { url }
     *  - greenhouse_board:        { boardSlug, filterTerms[]?, locationContains? }
     *  - lever_board:             { boardSlug, filterTerms[]?, locationContains? }
     *  - adzuna:                  { country? ('us'|'au', default 'us'), what, where?, resultsPerPage? }
     *  - usajobs:                 { keyword?, locationName?, resultsPerPage? }
     */
    config: jsonb('config').$type<Record<string, unknown>>().notNull(),
    enabled: boolean('enabled').default(true).notNull(),
    lastPolledAt: timestamp('last_polled_at', { withTimezone: true }),
    lastJobsFound: integer('last_jobs_found'),
    lastError: text('last_error'),
    /** W1-11(b) — the two-pillar shape: a per-user watchlist row becomes a
     *  SUBSCRIPTION referencing a shared atsRegistry entry when its kind is
     *  one the registry tracks (greenhouse_board/lever_board today). Additive
     *  + nullable: every row that predates this column (hand-added sources,
     *  seek_rss/adzuna/usajobs/discovery_email/rss_generic rows that have no
     *  registry concept) keeps working unmodified — registryId absent simply
     *  means "poll this row the old way" (lib/discovery.ts's existing
     *  per-user pollSource call), never a broken state. No FK constraint at
     *  the Drizzle layer (this codebase's convention — see jobs.userId,
     *  jobExtractions.jobId — relations are enforced in application code,
     *  not the schema); the SQL file adds a real FK for referential
     *  integrity at the Postgres layer. */
    registryId: uuid('registry_id'),
    /**
     * W1-12 (docs/briefs/launch-week-briefs.md §A): free-form provenance bag,
     * additive + optional so every pre-existing row (all hand-made) reads back
     * `null` — never a fabricated retrofit. The one key the derivation engine
     * writes today is `autoDerived: true`, which marks a row as machine-
     * generated from the user's Get Found desiredRoles (lib/watchlistDerive.ts)
     * so a regen pass can find + refresh/disable exactly those rows and never
     * touch a hand-made one. Room for future provenance (discoveredVia, etc.)
     * without another migration.
     */
    metadata: jsonb('metadata').$type<Record<string, unknown> | null>(),
    /** D-2 test-data flag (docs/BUILD-PLAN-2026-07-04.md, Reserved call 1):
     *  stamped `true` only when the OWNING user carried
     *  `account_kind IN ('demo','qa')` at the moment this source row was
     *  created (threaded from auth/session into the create-source path).
     *  Defaults `false` — every pre-existing row reads as real (NO
     *  INVENTION). watchlist_sources is per-user already (userId scopes
     *  every read today), so this column is primarily a housekeeping/audit
     *  signal + parity with the other 4 shared/pooled tables the D-2 flag
     *  covers, rather than a wall this table's OWN reads need — a real
     *  user's watchlist reads are already scoped to their own rows and can
     *  never surface a demo user's source. It still rides the same
     *  lib/testDataWall.ts helper for consistency, so a future shared-read
     *  surface over this table (e.g. an admin/ops view) inherits the wall
     *  for free. */
    isTestData: boolean('is_test_data').notNull().default(false),
    /** D18 (docs/design/fix-architecture-2026-07-08/{FIX-ARCHITECTURE.md,
     *  BUILD-PLAN.md}, Class 8's gut-check micro-loop) — when this standing
     *  subscription was last put in front of the owning user for a "still
     *  want this?" re-confirmation. Additive + nullable: every pre-existing
     *  row (including Jason's own 7 hand-tuned adzuna rows, and any
     *  greenhouse_board row like his Anthropic pull) reads back `null` —
     *  "never gut-checked," never a fabricated past confirmation. A source
     *  reused this column instead of a new table on purpose: the gut-check
     *  is state ABOUT this exact subscription row, not a separate ledger
     *  entity, and this table is ALREADY per-user (userIdx above) — no new
     *  join needed for the periodic-check unit to find candidates
     *  (`gutCheckedAt IS NULL OR gutCheckedAt < now() - interval`). */
    gutCheckedAt: timestamp('gut_checked_at', { withTimezone: true }),
    /** 'keep' | 'adjusted' | 'stopped' — the owning user's answer to the last
     *  gut-check ask, or `null` before the first ask/on a fresh row. varchar,
     *  not a DB enum, this codebase's own house convention (see file header's
     *  kind column, and claim_corrections.ruling's identical choice). A
     *  'stopped' row is NOT deleted (no-deletions law) — the poller reads
     *  `enabled` (flipped to false by the same act) to actually stop
     *  polling; this column is the AUDIT of why, kept alongside the
     *  already-existing enabled/lastError housekeeping fields. */
    gutCheckStatus: varchar('gut_check_status', { length: 16 }),
    ...timestamps,
  },
  (t) => ({
    userIdx: index('watchlist_sources_user_idx').on(t.userId),
    registryIdx: index('watchlist_sources_registry_idx').on(t.registryId),
  }),
);

/** The three answers a person can give the gut-check ask (Class 8, D18). */
export type WatchlistGutCheckStatus = 'keep' | 'adjusted' | 'stopped';
